import { useState } from "react";
import { Body } from "@sprout/typography";
import { TextInput } from "@sprout/text_input";
import "./support_dialog.scss";
import { useShowFlash } from "@sprout/flash";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog.tsx";
import { Trans, useTranslation } from "react-i18next";
import { Stack } from "@sprout/layout.tsx";
import { type Option, SingleSelect } from "@sprout/select";
import axios from "axios";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";

type Props = {
  bugReportPath: string;
  isOpen: boolean;
  setIsDialogOpen: (isOpen: boolean) => void;
};

const SupportDialog = ({ bugReportPath, isOpen, setIsDialogOpen }: Props) => {
  const { t } = useTranslation("support");
  const showFlash = useShowFlash();

  const [bugReport, setBugReport] = useState("");
  const [bugUrl, setBugUrl] = useState("");

  const topicOptions: Option[] = [
    { label: t("topics.general_feedback"), value: "general_feedback" },
    { label: t("topics.report_a_bug"), value: "report_a_bug" },
    { label: t("topics.feature_request"), value: "feature_request" },
  ];
  const [topic, setTopic] = useState<Option>(topicOptions[0]);

  const resetForm = () => {
    setBugReport("");
    setBugUrl("");
    setTopic(topicOptions[0]);
  };

  const submitBugReport = async () => {
    let reportContent = `Topic: ${topic.value} `;

    if (topic.value === "report_a_bug" && bugUrl) {
      reportContent += `Bug URL: ${bugUrl} `;
    }

    reportContent += `Report: ${bugReport.trim()}`;

    try {
      await axios.post(bugReportPath, {
        bug_report: reportContent,
      });

      resetForm();
      showFlash(t("feedback_submitted"));
    } catch (e) {
      showFlash(t("something_went_wrong"), "error");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            <Trans i18nKey="support:have_feedback" />
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Stack gap="8px" alignItems="stretch">
            <Body>
              <Trans i18nKey="support:help_make" />
            </Body>
            <SingleSelect
              label={t("topic")}
              options={topicOptions}
              onChange={(value) => setTopic(value!)}
              value={topic}
              portalMenu={false}
            />
            {topic.value === "report_a_bug" && (
              <TextInput
                id="bugUrl"
                type="text"
                label={t("bug_url")}
                onChange={(event) => setBugUrl(event.currentTarget.value)}
                value={bugUrl}
              />
            )}
            <TextInput
              id="bugReport"
              type="text"
              label={t(`instructions.${topic.value}`)}
              onChange={(event) => setBugReport(event.currentTarget.value)}
              value={bugReport}
              rows={4}
              multiline
              required
              help={topic.value === "report_a_bug" ? t("tell_us") : ""}
            />
          </Stack>
        </DialogDescription>
        <DialogFooter>
          <DialogClose>
            <SecondaryButton onClick={() => resetForm()}>
              <Trans i18nKey="support:cancel" />
            </SecondaryButton>
          </DialogClose>
          <DialogClose>
            <PrimaryButton
              onClick={submitBugReport}
              disabled={!bugReport || bugReport.length === 0}
            >
              <Trans i18nKey="support:share_feedback" />
            </PrimaryButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupportDialog;
